import {AxiosInstance} from 'axios';

import type {SectionSearchHit} from '../types';

export function createDocsApi(client: AxiosInstance) {
    return {
        // Documentation API
        async listDocs() {
            const response = await client.get('/api/docs');
            return response.data;
        },
        async getDoc(docId: string) {
            const response = await client.get(`/api/docs/${docId}`);
            return response.data;
        },
        async getDocConfig(docId: string) {
            const response = await client.get(`/api/docs/${docId}/config`);
            return response.data;
        },
        async deleteDoc(docId: string): Promise<{doc_id: string; deleted: boolean; counts: Record<string, number>}> {
            const response = await client.delete(`/api/docs/${docId}`);
            return response.data;
        },
        /** SB-144 — `planid` is sent only when the key is PRESENT, and `null` is a real
         *  value (clear the tie). The backend tells the two apart by sent-ness, so a
         *  title-only rename must not carry a `planid` key at all. */
        async updateDoc(
            docId: string,
            updates: {
                title?: string; planid?: number | null;
                /** SB-145 — the role beside the planid; same sent-ness rule, `null` clears.
                 *  `bestemmelser` is the one value that changes live verdicts. */
                role?: string | null;
                role_evidence?: Record<string, unknown> | null;
            },
        ): Promise<{doc_id: string; title: string; updated: boolean; planid: number | null; role: string | null}> {
            const response = await client.patch(`/api/docs/${docId}`, updates);
            return response.data;
        },
        async listSections(docId: string) {
            const response = await client.get(`/api/docs/${docId}/sections`);
            return response.data;
        },
        /** `links: 'resolved'` has the server point each § link at the section it cites, or leave it
         *  as text (SB-242). A stored anchor is no section id: `#SAK10-3-1` opens nothing. */
        async getSection(sectionId: string, render: boolean = true, links: 'stored' | 'resolved' = 'stored') {
            const response = await client.get(`/api/docs/sections/${sectionId}`, {params: {render, links}});
            return response.data;
        },
        /** The keyword search over sections, accents ignored, heading matches first (SB-396). */
        async searchSections(query: string, docId?: string, limit?: number): Promise<SectionSearchHit[]> {
            const params: any = {q: query};
            if (docId) params.doc_id = docId;
            if (limit) params.limit = limit;
            const response = await client.get('/api/docs/search', {params});
            return response.data;
        },
        async listAllSections(docId?: string, limit?: number) {
            const params: any = {};
            if (docId) params.doc_id = docId;
            if (limit) params.limit = limit;
            const response = await client.get('/api/docs/sections', {params});
            return response.data;
        },

        // Ingestion API
        async ingestTEK17Section(url: string) {
            const response = await client.get('/api/ingest/tek17', {params: {url}});
            return response.data;
        },
        async ingestTEK17Full() {
            const response = await client.get('/api/ingest/tek17/full');
            return response.data;
        },
        async ingestSAK10Section(url: string) {
            const response = await client.get('/api/ingest/sak10', {params: {url}});
            return response.data;
        },
        async ingestSAK10Full() {
            const response = await client.get('/api/ingest/sak10/full');
            return response.data;
        },
        async ingestPBLSection(url: string) {
            const response = await client.get('/api/ingest/pbl', {params: {url}});
            return response.data;
        },
        async ingestPBLFull() {
            const response = await client.get('/api/ingest/pbl/full');
            return response.data;
        },
    };
}
